import cx from "classnames";
import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { APP_LAYOUT } from "../../contexts/base-config.context";
import { ToastTypes, withToast } from "../../contexts/toastr.context";
import { useAppService } from "../../hooks/use-app-service";
import { Community } from "../../types/community/community.type";
import { User } from "../../types/user/minimal-user.type";
import { Button, TextInput } from "../../_components";
import { AppLayoutWrapper } from "../../_components/app-layout-wrapper";
import I18 from "../../_components/atoms/i18";
import CustomRadioComponent from "../../_components/form-controls/custom-radio.component";
import IconButton2 from "../../_components/icon-button-2-component";
import CropImageComponent from "../../_components/image-crop/crop-image-component";
import { MaxWidthWrapper } from "../../_components/max-width-wrapper";
import { NL2BR } from "../../_components/nl2br.component";
import { CommunityService } from "../../_service";
import { setCommunity } from "../../_store/_actions/community.actions";

export const COMMUNITY_BANNER_SETTINGS_PAGE_ROUTE = "/settings/homepage";

interface Props {
  addToast: any;
  community: Community;
  user: User;
  setCommunity: (community: Community) => void;
}
function CommunityBannerSettings({
  addToast,
  community,
  user,
  setCommunity,
}: Props) {
  return (
    <AppLayoutWrapper layout={APP_LAYOUT.TWO_COLUMN}>
      <MaxWidthWrapper className="max-w-screen-md">
        <div className="CommunityBannerSettings mx-4 min-h-screen px-4 pt-4">
          <CommunityBannerSettingsComponent
            addToast={addToast}
            community={community}
            setCommunity={setCommunity}
            user={user}
          />
        </div>
      </MaxWidthWrapper>
    </AppLayoutWrapper>
  );
}

/**
 * Component to edit community banner
 * @param {object} community
 * @param {function} setCommunity
 * @param {object} user
 * @param {function} addToast
 * @param {function} setIsCommunityBannerEditModalVisible
 */
interface ComponentProps {
  addToast: any;
  community: any;
  setCommunity: (community: any) => void;
  user: any;
  setIsCommunityBannerEditModalVisible?: any;
}
export function CommunityBannerSettingsComponent({
  addToast,
  community,
  setCommunity,
  user,
  setIsCommunityBannerEditModalVisible,
}: ComponentProps) {
  const [banner, setBanner] = useState(null);
  const [image, setImage] = useState(community?.banner);
  const [bannerTitle, setBannerTitle] = useState(
    community && community.bannerTitle
  );
  const [isBannerRemoved, setIsBannerRemoved] = useState(false);
  const [bannerDescription, setBannerDescription] = useState(
    (community && community.bannerDescription) ?? ""
  );

  const [layoutType, setLayoutType] = useState(
    community?.expandPostDirectory ? "list" : "compact"
  );

  const { analyticsService } = useAppService();

  const [isLoading, setIsLoading] = useState(false);

  const displayInModal = setIsCommunityBannerEditModalVisible ? true : false;

  const textAreaElement = useRef<HTMLTextAreaElement>(null);

  let rows = bannerDescription.split("\n").length;
  rows = rows > 3 ? rows : 3;

  const [isFormSubmittable, setIsFormSubmittable] = useState(true); // to disable submit button

  const resetFormAndClose = () => {
    if (displayInModal) {
      setIsCommunityBannerEditModalVisible(false);
    }
    setBanner(null);
    setBannerTitle(community.bannerTitle ?? "");
    setBannerDescription(community.bannerDescription ?? "");
  };

  useEffect(() => {
    if (!community) return;
    setBannerTitle(community.bannerTitle ?? "");
    setBannerDescription(community.bannerDescription ?? "");
  }, [community]);

  const submitForm = async (e: any) => {
    // update the details of the community
    setIsLoading(true);
    if (
      community.expandPostDirectory !== (layoutType === "list" ? true : false)
    ) {
      await updateHomeFeedLayout();
    }
    CommunityService.updateCommunityBannerDetails(
      community.id,
      bannerTitle,
      bannerDescription,
      user
    )
      .then(({ community }) => {
        // check if need to upload banner image
        if (image && typeof image !== "string") {
          CommunityService.updateCommunityBanner(community.id, user, image)
            .then(({ community }) => {
              addToast(
                "Banner details updated!",
                "Refresh if changes are not visible!"
              );
              resetFormAndClose();
              setCommunity(community);
              setIsLoading(false);
            })
            .catch((error) => {
              addToast(
                "Banner details could not be updated!",

                "",
                ToastTypes.danger
              );
              console.log("error occurred!", { error });
              setIsLoading(false);
            });
        } else if (isBannerRemoved) {
          // Remove community banner
          CommunityService.removeCommunityBanner(user, community.id)
            .then(({ community }) => {
              addToast(
                "Banner details updated!",
                "Refresh if changes are not visible!"
              );
              resetFormAndClose();
              setCommunity(community);
              setIsLoading(false);
            })
            .catch((error) => {
              addToast(
                "Banner details could not be updated!",
                "",
                ToastTypes.danger
              );
              console.log("error occurred!", { error });
              setIsLoading(false);
            });
        } else {
          addToast(
            "Banner details updated!",
            "Refresh if changes are not visible!"
          );
          resetFormAndClose();
          setIsLoading(false);
          setCommunity(community);
        }
        analyticsService.track("community-banner-update", {
          ...(community.bannerTitle !== "" && {
            bannerTitle: community.bannerTitle,
          }),
          ...(community.bannerDescription !== "" && {
            bannerDescription: community.bannerDescription,
          }),
          ...(community.banner !== "" && { banner: community.banner }),
        });
      })
      .catch((error) => {
        addToast("Banner details could not be updated!", "", ToastTypes.danger);
        console.log("error occurred!", { error });
        setIsLoading(false);
      });
  };

  async function updateHomeFeedLayout() {
    CommunityService.updateConfig(
      user,
      community.id,
      "expandPostDirectory",
      layoutType === "list" ? true : false
    )
      .then(({ community }) => {
        setCommunity(community);
        setIsLoading(false);
        analyticsService.track("default-expanded-post" as any, {
          checkboxSelected: "expandPostDirectory",
        });
      })
      .catch((err) => {
        addToast("Failed to do so.", "", ToastTypes.danger);
        setIsLoading(false);
        // setOperationSuccessful(false);
      });
  }

  if (!community) {
    return null;
  }
  return (
    <div className="CommunityBannerSettingsComponent rounded-xl bg-card shadow">
      {/* headers */}
      <div className="flex items-center justify-between rounded-2xl bg-card py-2 pl-4 pr-2">
        <span className="flex flex-row items-center gap-0.5 font-bold text-secondary-foreground/85">
          <span>
            <I18>Community Banner</I18>
          </span>
        </span>
        {displayInModal ? (
          <IconButton2
            icon="cross"
            size="md"
            onClick={() => {
              setIsCommunityBannerEditModalVisible(false);
            }}
          />
        ) : (
          <div className="h-10"></div>
        )}
      </div>
      {/* <Switch label="Enable Community Banner" className="mx-4" /> */}
      {/* form controls */}
      <div className="mx-4 mb-4 flex flex-col rounded-xl bg-background px-5 pb-8 pt-5">
        {/* banner */}
        <div className="banner-selector-holder overflow-scroll rounded">
          <div
            className={cx("banner-selector cursor-pointer", {
              selected: image || community.banner,
              "items-center justify-center": !banner && !community.banner,
            })}>
            {/* {banner || community.banner ? ( */}
            <div className="flex w-full flex-col">
              <CropImageComponent
                picture={image}
                setPicture={setImage}
                setPictureSelected={(selected: any) => setBanner(selected)}
                setIsPictureRemoved={setIsBannerRemoved}
                aspectRatio={16 / 6}
                recommendedRatio={"16:6"}
                cropRatioMessage={"Banner Ratio - 16:6"}
                displayUnsplashSearch={true}
              />
              <div className="mt-2 px-4 text-lg font-semibold">
                {bannerTitle ? bannerTitle : ""}
              </div>
              <div className="px-4">
                <NL2BR text={bannerDescription} />
              </div>
            </div>
          </div>
        </div>
        {/* name */}
        <div className="mt-4 font-semibold text-secondary-foreground/85">
          <I18>Banner Title</I18>
        </div>
        <TextInput
          placeholder="Enter here"
          validator={(value) => {}}
          defaultValue={bannerTitle}
          className="bg-card pt-2 focus:outline-none"
          onChange={(value) => {
            setBannerTitle(value);
          }}
        />
        <div className="text-xs font-light text-secondary-foreground/70">
          <I18>Suggestions: Welcome to my community etc</I18>
        </div>

        {/* banner description */}
        <>
          <div className="mt-4 font-semibold text-secondary-foreground/85">
            <I18>Banner Description</I18>
          </div>
          <textarea
            ref={textAreaElement}
            value={bannerDescription}
            onChange={(e) => {
              setBannerDescription(e.target.value);
              window.setTimeout(() => {
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }, 0);
            }}
            className="my-3 block w-full flex-grow rounded border border-border bg-card px-4 py-2 focus:outline-none"
            rows={rows}
            placeholder={"Enter Banner Description"}
          />
        </>
      </div>
      <div
        className={cx("mx-4 flex gap-4", {
          "mb-4": displayInModal,
        })}>
        <CustomRadioComponent
          selectorTypeLabel="Homefeed Layout"
          selectedOption={layoutType}
          setSelectedOption={setLayoutType}
          options={[
            {
              id: "list",
              iconName: "menu-burger",
              selectorName: "List View",
              origin: "",
              tooltips: [],
            },
            {
              id: "compact",
              iconName: "list",
              selectorName: "Compact View",
              origin: "",
              tooltips: [],
            },
          ]}
        />
      </div>
      {/* submit button */}
      <div
        className={cx("px-4 py-3", {
          "border-t border-border": displayInModal,
        })}>
        <div
          className={cx("mx-auto flex items-center", {
            "justify-between": displayInModal,
            "justify-end": !displayInModal,
          })}>
          {displayInModal && (
            <Button
              onClick={(e) => {
                resetFormAndClose();
              }}
              flat
              className="border-primary"
              label="Cancel"
              large
            />
          )}

          <Button
            disabled={!isFormSubmittable}
            onClick={submitForm}
            label={"Save"}
            large
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
}

const stp = (state: any) => ({
  user: state.auth,
  community: state.community,
  communityPac: state.communityPac,
  setCommunity: state.updateCommunity,
});

const dtp = (dispatch: any) =>
  bindActionCreators({ setCommunity: setCommunity }, dispatch);

const CommunityBannerSettingsPage = withToast(
  connect(stp, dtp)(CommunityBannerSettings)
);

export default CommunityBannerSettingsPage;
